import React, { useCallback, useEffect, useMemo, useState } from 'react';
import inventoryService from '../services/inventoryService';
import inventoryLedgerService from '../services/inventoryLedgerService';
import { useStore } from '../context/StoreContext';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { formatInStoreTimezone, toApiDateValue } from '../utils/dateUtils';
import useStoreTimezone from '../hooks/useStoreTimezone';

const MOVEMENT_TYPES = [
  { value: 'all', label: 'All movements' },
  { value: 'Sale', label: 'Sale' },
  { value: 'Refund', label: 'Refund' },
  { value: 'PO_APPROVED', label: 'PO Approved' },
  { value: 'PO_RECEIPT', label: 'PO Receipt' },
  { value: 'Transfer-Out', label: 'Transfer Out' },
  { value: 'Transfer-In', label: 'Transfer In' },
  { value: 'Adjustment', label: 'Adjustment' },
  { value: 'Initial', label: 'Initial Stock' },
  { value: 'Import', label: 'Import' },
];

const defaultDateFilters = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);
  return {
    period: 'month',
    startDate: toApiDateValue(start),
    endDate: toApiDateValue(end),
  };
};

const InventoryLedgerPage = () => {
  const { activeStore } = useStore();
  const storeId = activeStore?._id;
  const timezone = useStoreTimezone();

  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [movementType, setMovementType] = useState('all');
  const [dateFilters, setDateFilters] = useState(defaultDateFilters);

  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedProductId = selectedProduct?._id || '';

  // Search products as user types
  useEffect(() => {
    if (!storeId) {
      setProductResults([]);
      setSelectedProduct(null);
      return;
    }
    const term = productQuery.trim();
    if (!term) {
      setProductResults([]);
      return;
    }
    const handler = setTimeout(async () => {
      try {
        setProductLoading(true);
        const results = await inventoryService.searchProducts(storeId, term);
        setProductResults(results || []);
      } catch (err) {
        console.error('Failed to search products', err);
      } finally {
        setProductLoading(false);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [productQuery, storeId]);

  const fetchLedger = useCallback(
    async (page = 1) => {
      if (!storeId || !selectedProductId) {
        setLedgerEntries([]);
        setPagination({ page: 1, pages: 1, total: 0 });
        return;
      }
      try {
        setLoading(true);
        setError('');
        const params = {
          productId: selectedProductId,
          page,
          ...(dateFilters.period ? { period: dateFilters.period } : {}),
          ...(dateFilters.startDate ? { startDate: dateFilters.startDate } : {}),
          ...(dateFilters.endDate ? { endDate: dateFilters.endDate } : {}),
          ...(movementType !== 'all' ? { type: movementType } : {}),
        };
        const res = await inventoryLedgerService.getLedgerEntries(params);
        setLedgerEntries(res.data || []);
        setPagination(res.pagination || { page, pages: 1, total: 0 });
      } catch (err) {
        setError(err.response?.data?.message || 'Could not load ledger entries.');
      } finally {
        setLoading(false);
      }
    },
    [storeId, selectedProductId, movementType, dateFilters]
  );

  useEffect(() => {
    fetchLedger(1);
  }, [fetchLedger]);

  const handleExport = useCallback(() => {
    if (!ledgerEntries.length) return;
    const header = ['Date', 'Type', 'Change', 'Balance', 'User', 'Reference'];
    const rows = ledgerEntries.map((entry) => [
      formatInStoreTimezone(entry.createdAt, 'dd-MM-yyyy HH:mm', timezone),
      entry.type,
      entry.change,
      entry.newStockLevel,
      entry.user?.name || '',
      entry.reference || '',
    ]);
    const csv = [header, ...rows]
      .map((line) => line.map((cell) => `"${cell ?? ''}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `inventory-ledger-${selectedProduct?.sku || 'product'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [ledgerEntries, timezone, selectedProduct]);

  const productSummary = useMemo(() => {
    if (!selectedProduct) return null;
    return (
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <p className="text-sm text-gray-500 uppercase tracking-wide">Viewing history for</p>
            <p className="font-semibold text-lg text-gray-900">{selectedProduct.name}</p>
            <p className="text-sm text-gray-500">
              SKU: {selectedProduct.sku || '—'} • Barcode: {selectedProduct.barcode || '—'}
            </p>
          </div>
          {ledgerEntries.length > 0 && (
            <button
              onClick={handleExport}
              className="px-4 py-2 rounded-full bg-slate-800 text-white text-sm font-semibold hover:bg-slate-700"
            >
              Export CSV
            </button>
          )}
        </div>
      </div>
    );
  }, [selectedProduct, ledgerEntries.length, handleExport]);

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Inventory Ledger</h1>
          <p className="text-gray-500">Track every stock movement for your products.</p>
        </div>
        {selectedProduct && (
          <button
            onClick={() => setSelectedProduct(null)}
            className="text-sm font-semibold text-gray-600 hover:text-primary"
          >
            Clear selection
          </button>
        )}
      </div>

      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}

      <div className="bg-white rounded-2xl shadow p-4 mb-4 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
              Search product
            </label>
            <input
              type="text"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
              placeholder="Type name, SKU, or barcode"
              className="w-full border rounded-md px-3 py-2 text-sm"
              disabled={!storeId}
            />
            {productLoading && <p className="text-xs text-gray-500 mt-1">Searching…</p>}
            {productResults.length > 0 && (
              <div className="mt-2 border rounded-lg max-h-56 overflow-y-auto divide-y">
                {productResults.map((product) => (
                  <button
                    key={product._id}
                    onClick={() => {
                      setSelectedProduct(product);
                      setProductResults([]);
                      setProductQuery(product.name);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                  >
                    <p className="font-semibold text-gray-900">{product.name}</p>
                    <p className="text-xs text-gray-500">
                      SKU: {product.sku || '—'} • Barcode: {product.barcode || '—'}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
              Movement type
            </label>
            <select
              value={movementType}
              onChange={(e) => setMovementType(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              {MOVEMENT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div className="lg:col-span-1">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
              Date range
            </label>
            <DateRangeFilter
              selectedPeriod={dateFilters.period}
              startDate={dateFilters.startDate}
              endDate={dateFilters.endDate}
              onSelectPreset={(periodKey, range) =>
                setDateFilters({
                  period: periodKey,
                  startDate: range.start,
                  endDate: range.end,
                })
              }
              onApplyCustomRange={(start, end) =>
                setDateFilters({
                  period: 'custom',
                  startDate: start,
                  endDate: end,
                })
              }
            />
          </div>
        </div>
      </div>

      {productSummary}

      <div className="bg-white rounded-2xl shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Date</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Type</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-600">Change</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-600">Balance</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">User</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Reference</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {!selectedProduct && (
              <tr>
                <td colSpan={6} className="text-center py-10 text-gray-500">
                  Search and select a product to view its inventory history.
                </td>
              </tr>
            )}
            {selectedProduct && loading && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-500">
                  Loading ledger entries…
                </td>
              </tr>
            )}
            {selectedProduct && !loading && ledgerEntries.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-500">
                  No movements for the selected filters.
                </td>
              </tr>
            )}
            {ledgerEntries.map((entry) => (
              <tr key={entry._id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  {formatInStoreTimezone(entry.createdAt, 'dd-MM-yyyy hh:mm a', timezone)}
                </td>
                <td className="px-4 py-3 font-semibold text-slate-800">{entry.type}</td>
                <td
                  className={`px-4 py-3 text-right font-semibold ${
                    entry.change > 0 ? 'text-emerald-600' : 'text-rose-600'
                  }`}
                >
                  {entry.change > 0 ? `+${entry.change}` : entry.change}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-gray-900">
                  {entry.newStockLevel}
                </td>
                <td className="px-4 py-3 text-gray-600">{entry.user?.name || 'N/A'}</td>
                <td className="px-4 py-3 text-gray-600 font-mono text-xs">
                  {entry.reference || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedProduct && pagination.pages > 1 && (
        <div className="mt-4 flex justify-between items-center text-sm text-gray-600">
          <button
            onClick={() => fetchLedger(Math.max(1, pagination.page - 1))}
            disabled={pagination.page <= 1}
            className="px-4 py-2 rounded-full border border-gray-300 disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.pages} ({pagination.total} entries)
          </span>
          <button
            onClick={() => fetchLedger(Math.min(pagination.pages, pagination.page + 1))}
            disabled={pagination.page >= pagination.pages}
            className="px-4 py-2 rounded-full border border-gray-300 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default InventoryLedgerPage;
